<template>
    <ob1-popin-modal2
        :id="modalId"
        :aria-labelledby="modalLabelId"
        :show-close-button="false"
    >
        <div :id="modalLabelId" class="h4" v-html="messages.title" />

        <div id="result" class="m-auto mt-1 mb-2">
            <div id="total" class="bg-gray2 p-1 px-2 mb-1">
                <span class="big">{{ totalFormatted }}</span>
                <small class="text-start ps-1">pour la reprise de votre<br><strong
                    v-html="estimatedDeviceModel"
                /></small>
            </div>
            <div v-if="bonus && bonus.amountInCents > 0" id="with-bonus">
                <div class="result-detail bg-gray2 p-1">
                    <div class="big">
                        {{ recoveryFormatted }}
                    </div>
                    <small>pour votre appareil</small>
                </div>
                <div class="result-plus p-1 big">
                    +
                </div>
                <div class="result-detail bg-gray2 p-1">
                    <div class="big">
                        {{ bonusFormatted }}
                    </div>
                    <small>de bonus reprise</small>
                </div>
            </div>

            <AlertMessage
                class="mt-1 text-start"
                type="warning"
                is-mini
                :title="messages.warningMessage"
                :bold-title="false"
            />
        </div>

        <hr class="separator separator--gray4 mb-1" aria-hidden="true">

        <p>
            <strong v-html="labels.subtitle.text " />
        </p>
        <div v-if="labels.subtitle.link">
            <Lien
                is-link-arrow
                open-new-tab
                :href="labels.subtitle.link.url"
                :label="labels.subtitle.link.label"
            />
        </div>

        <ul class="list-unstyled mt-1">
            <li class="text-mw mb-1">
                <div class="items-list-item highlight">
                    <span class="item-icon" :class="labels.firstItem.icon" aria-hidden="true" />
                    <p class="mb-0" v-html="labels.firstItem.text" />
                </div>
            </li>
            <li class="text-mw mb-1">
                <div class="items-list-item highlight">
                    <span class="item-icon" :class="labels.secondItem.icon" aria-hidden="true" />
                    <p class="mb-0" v-html="labels.secondItem.text" />
                </div>
            </li>
        </ul>

        <AlertMessage
            v-if="labels.bonASavoir"
            :title="labels.bonASavoir.text"
            :no-background="true"
            :bold-title="false"
        />

        <CheckBox
            v-if="showDipliConsent"
            :id="modalId + '-dipli-consent'"
            :model-value="isDipliConsentChecked"
            :label="labels.dipliConsent.text"
            class="pb-1"
            @change="onCheckboxChange"
        />

        <div id="sticky-container">
            <div class="bg-white pb-1">
                <hr class="separator separator--gray4 mb-1 d-md-none m-auto" aria-hidden="true">
                <div id="buttons-container">
                    <Lien
                        id="other-simulation-button"
                        is-link-arrow-back
                        is-mini
                        is-button
                        :label="messages.otherSimulationButtonLabel"
                        @click="newSimulation()"
                    />
                    <button
                        id="continue-button"
                        type="button"
                        href="#"
                        class="d-block btn btn-primary"
                        @click="saveAndClose()"
                        v-html="messages.buttonLabel"
                    />
                </div>
            </div>
        </div>
    </ob1-popin-modal2>
</template>

<script>

import AlertMessage from "../../common/components/ob1/AlertMessage.vue";
import Lien from "../../common/components/ob1/Lien.vue";
import CheckBox from "../../common/components/ob1/CheckBox.vue";

export default {
    name: "MobileRecoverySimulationResultModal",
    components: {CheckBox, Lien, AlertMessage },
    props: {
        modalId: {
            type: String,
            required: true
        },
        shop: {
            type: String,
            required: true
        },
        messages: {
            type: Object,
            required: true
        },
        estimatedDeviceModel: {
            type: String,
            required: false,
            default: ""
        },
        estimatedDevicePriceInCents: {
            type: Number,
            required: false,
            default: 0
        },
        bonus: {
            type: Object,
            default: undefined
        },
        onlineRecoveryAvailable: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isDipliConsentChecked: false
        };
    },
    computed: {
        modalLabelId: function() {
            return this.modalId + "-label";
        },
        labels: function() {
            let messages = {
                subtitle: {
                    text: this.messages.subtitle,
                    link: undefined
                },
                firstItem: {
                    icon: this.messages.firstItemIcon,
                    text: this.messages.firstItemText
                },
                secondItem: {
                    icon: this.messages.secondItemIcon,
                    text: this.messages.secondItemText
                },
                bonASavoir: {
                    text: this.messages.bonASavoirText
                },
                dipliConsent : {
                    text : this.messages.dipliConsentLabel
                }
            };

            if (0 < this.estimatedDevicePriceInCents && this.estimatedDevicePriceInCents < 1000) {
                messages.firstItem.text = this.messages.firstItemNoRecoveryText;
                messages.secondItem.text = this.messages.secondItemLowPriceText;
            } else if (this.estimatedDevicePriceInCents === 0) {
                messages.subtitle.text = this.messages.subtitleRecyclage;
                messages.subtitle.link = {
                    label: this.messages.subtitleRecyclageLinkLabel,
                    url: this.messages.subtitleRecyclageLinkUrl
                };
                messages.firstItem.text = this.messages.firstItemNoRecoveryText;
                messages.secondItem.text = this.messages.secondItemRecyclageText;
            }

            return messages;
        },
        totalFormatted: function() {
            let bonusValue = this.bonus ? this.bonus.amountInCents : 0;
            return PriceFormatter.formatEurosWithSpace((this.estimatedDevicePriceInCents + bonusValue) / 100, false).toString();
        },
        bonusFormatted: function() {
            let bonusValue = this.bonus ? this.bonus.amountInCents : 0;
            return PriceFormatter.formatEurosWithSpace(bonusValue / 100, false).toString();
        },
        recoveryFormatted: function() {
            return PriceFormatter.formatEurosWithSpace(this.estimatedDevicePriceInCents / 100, false).toString();
        },
        showDipliConsent() {
            let bonusValue = this.bonus ? this.bonus.amountInCents : 0;
            let totalRecovery = this.estimatedDevicePriceInCents + bonusValue;
            return this.onlineRecoveryAvailable && totalRecovery > 0;
        }
    },
    mounted: function() {
        $(`#${this.modalId}`).on("hidden.bs.modal", () => {
            this.resetState();
        });
    },
    beforeDestroy: function () {
        const $modal = $(`#${this.modalId}`);
        $modal.off("hidden.bs.modal");
    },
    methods: {
        saveAndClose: function() {
            this.saveSimulation(this.shop, this.isDipliConsentChecked)
                .then(() => {
                    $("#" + this.modalId).modal("hide");
                    this.$emit("save-simulation");
                });
        },
        newSimulation: function() {
            this.$emit("new-simulation");
        },
        saveSimulation: function(shop, isDipliConsentChecked) {
            return $.ajax({
                method: "POST",
                data: {
                    "shop": shop,
                    "withDipliConsent": isDipliConsentChecked
                },
                url: "/api/recovery/saveSimulation"
            });
        },
        onCheckboxChange(isChecked) {
            this.isDipliConsentChecked = isChecked;
            Tms.link({
                category: 'estimer_votre_appareil',
                action: 'transfert_coordonnees_a_dipli',
                label: isChecked ? 'coche_dipli' : 'decoche_dipli'
            });
        },
        resetState() {
            this.isDipliConsentChecked = false;
        }
    }
};
</script>

<style lang="scss">
@import 'ob1_common';
@import 'ob1_variables';

#sticky-container {
    @media (max-width: #{map-get($grid-breakpoints, md)}) {
        position: sticky;
        bottom: 0px;
        z-index: 3;
    }
}

#other-simulation-button {
    width: auto;
}

#result {
    max-width: 40vh;
    text-align: center;


    #with-bonus {
        display: flex;
        justify-content: space-between;
        gap: px-to-rem(10);
        align-items: center;

        .result-detail {
            flex-grow: 2;
        }

        .result-plus {
            flex-grow: 1;
        }
    }

    #total {
        display: flex;
        justify-content: center;
    }

    .big {
        font-size: px-to-rem(24);
        font-weight: bold;
        padding: px-to-rem(10);
        white-space: nowrap
    }
}

#buttons-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: px-to-rem(10);

    @media (max-width: #{map-get($grid-breakpoints, md)}) {
        width: 100%;
        flex-direction: column-reverse;
    }
}

</style>

<style lang="scss" scoped>
@import 'ob1_common';

.items-list-item {
    max-width: inherit;
    align-items: inherit;

    .item-icon {
        font-size: px-to-rem(24);
        padding-top: px-to-rem(8);
    }
}

</style>
